import React, { FC, useCallback, useMemo, useRef, useState } from 'react';

import { DateTime } from 'luxon';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import {
    BaseStyle,
    BaseStyleEnums,
    ButtonType,
    DatePicker,
    DatePickerEnums,
    TimeZoneEnums,
} from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';
import { useGamePolling } from '@nhl/react-hooks/lib/useGamePolling';
import { useScroll } from '@nhl/react-hooks/lib/useScroll';

import commonMessages from '~/core/commonMessages';
import { DAY_MONTH_DATE, getDateRangeStringForWeek } from '~/core/dateTimeFormats';
import { useDateParam } from '~/core/hooks/useDateParam';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { isPageZoomed } from '~/core/utils';
import { defaultLocationData, useLocationQuery } from '~/redux/ducks/location';
import AdTag from '~/views/components/adTag/AdTag';
import { AdType } from '~/views/components/adTag/enums';
import BackToTopButton from '~/views/components/backToTopButton/BackToTopButton';
import DayCard from '~/views/components/dayCard/DayCard';
import { Days } from '~/views/components/dayCard/styles';
import NoContent from '~/views/components/noContent/NoContent';
import NarrowOddsProvider from '~/views/components/oddsProvider/NarrowOddsProvider';
import OddsProviderDayCard from '~/views/components/oddsProvider/OddsProviderDayCard';
import PageWrapper from '~/views/components/pageWrapper/PageWrapper';
import TimezonePicker from '~/views/components/timezonePicker/TimezonePicker';
import { SelectableTimezone } from '~/views/components/timezonePicker/types';

import {
    AdContainer,
    Controls,
    ExpandFilterButton,
    Filters,
    Header,
    LeftHandFilters,
    MobileDaysContainer,
    OddsProviderWrapper,
    SmallLeagueFilters,
} from '../schedule/styles';

import { useScoreQuery } from './api';
import Day from './components/day/Day';

import { Control, TimezonePickerWrapper } from './styles';

const Scores: FC = () => {
    const navigate = useNavigate();
    const dateFromURL = useDateParam();
    const intl = useIntl();
    const { data: countryCode = defaultLocationData } = useLocationQuery({});

    const size = useSizeWithThrottle();
    const isMedium = size.width >= BaseStyleEnums.ScreenWidthBreakpoints.medium;

    const [timezone, setTimezone] = useState<SelectableTimezone>();
    const [isStickyHeader, setIsStickyHeader] = useState<boolean>(false);
    const [isDisplayingTimezonePicker, setIsDisplayingTimezonePicker] = useState<boolean>(false);
    const [isShowingNarrowOdds, setIsShowingNarrowOdds] = useState<boolean>(true);

    const daysRef = useRef<HTMLDivElement>(null);

    const onScrollChange = useCallback(() => {
        const top = daysRef.current?.getBoundingClientRect().top || Infinity;
        const padding = isMedium ? 16 : 8;

        setIsStickyHeader(BaseStyle.LEAGUE_HEADER_HEIGHT + padding >= top && !isPageZoomed());
    }, [isMedium]);

    useScroll({
        onScrollChanged: onScrollChange,
    });

    const { data, isLoading, isInitialLoad, resolvedDate, refetch } = useScoreQuery(dateFromURL);

    const oddsPartner = data?.oddsPartners?.find((partner) => partner.country === countryCode);

    useGamePolling(refetch, data.games || []);

    const pageTitle = useMemo(() => {
        if (resolvedDate !== undefined) {
            return intl.formatMessage(
                {
                    description: 'Title for the League Scores page, to be displayed on a browser tab etc.',
                    defaultMessage: 'NHL Scores | {date} | NHL.com',
                },
                {
                    date: resolvedDate.toLocaleString(DateTime.DATE_MED),
                }
            );
        }

        return undefined;
    }, [intl, resolvedDate]);

    const noGamesText = <FormattedMessage {...commonMessages.misc.noGamesScheduled} />;

    const updateDate = (newDate: DateTime) => {
        navigate(`/scores/${newDate.toISODate()}`);
    };

    const handleArrowClick = (isLeft: boolean) => {
        if (resolvedDate !== undefined) {
            const newDate = resolvedDate.plus({ days: isLeft ? -7 : 7 }).toISODate();
            navigate(`/scores/${newDate}`);
        }
    };

    const daysDisplay = (
        <>
            <Days ref={daysRef} $padBottom={isStickyHeader && (isMedium || !isShowingNarrowOdds || !oddsPartner)}>
                {data?.gameWeek?.map((day) => {
                    // TODO fix the focusedDate???
                    const focusedDate = DateTime.fromISO(data.gameWeek[3].date);
                    const isToday = DateTime.fromISO(day.date).hasSame(focusedDate, 'day');
                    return (
                        <DayCard key={day.date} day={day} isSelected={isToday} isToday={isToday} urlRoot="/scores" />
                    );
                })}
                {isStickyHeader && oddsPartner && isMedium && <OddsProviderDayCard bettingOddPartner={oddsPartner} />}
            </Days>
            {isStickyHeader && oddsPartner && (
                <OddsProviderWrapper>
                    <NarrowOddsProvider
                        bettingOddPartner={oddsPartner}
                        show={isShowingNarrowOdds}
                        setShow={setIsShowingNarrowOdds}
                    />
                </OddsProviderWrapper>
            )}
        </>
    );

    const timezonePicker = (
        <TimezonePickerWrapper>
            <TimezonePicker onChangeTimezone={setTimezone} selectedTimezone={timezone} />
        </TimezonePickerWrapper>
    );

    return isInitialLoad ? (
        <LoadingDisplay isLoading={true} />
    ) : (
        <PageWrapper defaultPageLayout pageTitle={pageTitle} theme={BaseStyle.LightTheme}>
            <LoadingDisplay isLoading={isLoading}>
                <Controls $isStickyHeader={isStickyHeader}>
                    <Control>
                        <Header>
                            <FormattedMessage defaultMessage="Scores" description="Header for Scores page" />
                            {window.__AD_SETTINGS__.presbyDivId && (
                                <AdTag divId={window.__AD_SETTINGS__.presbyDivId} adType={AdType.PRESBY} />
                            )}
                        </Header>
                        <Filters $isStickyHeader={isStickyHeader}>
                            <LeftHandFilters>
                                <DatePicker
                                    dateDisplay={(date) => getDateRangeStringForWeek(date.minus({ days: 3 }), intl)}
                                    intl={intl}
                                    isDisplayingFullWeek={true}
                                    localeCode={window.__SITE_SETTINGS__.localeCode}
                                    minDetail={DatePickerEnums.CalendarViewType.year}
                                    onArrowClick={handleArrowClick}
                                    onSelectDate={updateDate}
                                    selectedDate={resolvedDate}
                                />
                                {isMedium && timezonePicker}
                            </LeftHandFilters>
                        </Filters>
                        {isMedium && daysDisplay}
                    </Control>
                    {window.__AD_SETTINGS__.mrecDivId && (
                        <AdContainer $isHidden={isStickyHeader}>
                            <AdTag divId={window.__AD_SETTINGS__.mrecDivId} adType={AdType.MEDIUM_RECTANGLE} />
                        </AdContainer>
                    )}
                </Controls>
                {!isMedium && (
                    <>
                        <MobileDaysContainer $isStickyHeader={isStickyHeader}>{daysDisplay}</MobileDaysContainer>
                        <SmallLeagueFilters>
                            {isDisplayingTimezonePicker && timezonePicker}
                            <ExpandFilterButton
                                disableHover={true}
                                variant={ButtonType.Text}
                                onClick={() => setIsDisplayingTimezonePicker(!isDisplayingTimezonePicker)}
                            >
                                <FormattedMessage
                                    {...commonMessages.misc.callToAction[
                                        isDisplayingTimezonePicker ? 'hideFilters' : 'showFilters'
                                    ]}
                                />
                            </ExpandFilterButton>
                        </SmallLeagueFilters>
                    </>
                )}
                {isStickyHeader &&
                    createPortal(
                        <BackToTopButton
                            onClick={() => document.getElementById('root')?.scrollIntoView({ behavior: 'smooth' })}
                        />,
                        document.body
                    )}
                {data.games.length === 0 ? (
                    <NoContent primaryText={noGamesText} />
                ) : (
                    <Day
                        key={data.currentDate}
                        games={data.games}
                        date={DateTime.fromISO(data.currentDate).toLocaleString(DAY_MONTH_DATE)}
                        timezone={timezone?.value || TimeZoneEnums.AMERICA_NEW_YORK}
                        countryCode={countryCode}
                        oddsPartners={data.oddsPartners}
                    />
                )}
            </LoadingDisplay>
        </PageWrapper>
    );
};

export { Scores as Component };
